import { Container, IconButton, Typography } from '@material-ui/core'
import { ArrowBack } from '@material-ui/icons'
import { Skeleton } from '@material-ui/lab'
import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import Button from '@/components/button'
import { useConfirmReportResolution } from '@/hooks/mutations/use-confirm-report-resolution'
import { FileToUpload } from '@/hooks/mutations/use-create-diagnosis/manager/upload-queue'
import { useCreateReportResolution } from '@/hooks/mutations/use-create-report-resolution'
import { useUploadQueue } from '@/hooks/mutations/use-upload-queue'
import { useGetCogUrlFromDiagnosis } from '@/hooks/queries/use-get-cog-url-from-diagnosis'
import { useGetLatestDiagnosisReport } from '@/hooks/queries/use-get-latest-diagnosis-report'
import { useGetPerimeterData } from '@/hooks/queries/use-get-perimeter-data'
import { useAuth } from '@/hooks/use-auth'
import { bytesToUnitString } from '@/pages/demands/utils/string-conversions'

import { LinearProgressWithLabel } from '../../components/linear-progress-with-label'
import { Map } from '../../components/map'
import { useDemandAnalysisStore } from '../../contexts/demand-analysis-store-context'
import { useDemandAnalysisRoute } from '../../hooks/use-demand-analysis-route'
import { useStyles } from './styles'

export function Upload() {
  const classes = useStyles()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [uploadProgress, setUploadProgress] = useState({
    perimeter: { progress: 0 },
    mosaic: { progress: 0 },
  })

  const { treatment, culture, diagnosisId, makeUrl } = useDemandAnalysisRoute()

  const { perimeterFromInput, mosaicFromInput, markFileAsDone } =
    useDemandAnalysisStore((state) => ({
      perimeterFromInput: state.perimeter,
      mosaicFromInput: state.mosaic,
      markFileAsDone: state.markFileAsDone,
    }))

  const { data: perimeterData } = useGetPerimeterData(diagnosisId!)

  const { data: cogUrl } = useGetCogUrlFromDiagnosis(culture!, diagnosisId!)

  const { data: report } = useGetLatestDiagnosisReport(
    treatment!,
    culture!,
    diagnosisId!,
  )

  const {
    mutate: createResolution,
    data: resolution,
    isSuccess: isResolutionCreated,
    isError: isResolutionError,
    reset: resetResolution,
  } = useCreateReportResolution(treatment!, culture!, diagnosisId!, report?.id!)

  const {
    mutate: uploadQueue,
    isSuccess: isUploadSuccessful,
    isError: isUploadError,
    reset: resetUploadQueue,
  } = useUploadQueue({
    onUploadProgressByInput: handleUploadProgressByInput,
    onFileDone: handleFileDone,
  })

  const {
    mutate: confirmResolution,
    isError: isConfirmResolutionError,
    isSuccess: isConfirmResolutionSuccessfull,
    reset: resetConfirmResolution,
  } = useConfirmReportResolution(
    treatment!,
    culture!,
    diagnosisId!,
    report?.id!,
  )

  function handleUploadProgressByInput(
    inputType: string,
    { progress }: { progress?: number; length?: number },
  ) {
    setUploadProgress((state) => {
      const type = inputType as keyof typeof state
      return {
        ...state,
        [type]: {
          ...state[type],
          progress: Number(progress),
        },
      }
    })
  }

  function handleFileDone(index: number) {
    const file = filesToUpload[index]
    if (!file) return

    markFileAsDone(file.inputType as 'perimeter' | 'mosaic')
  }

  function handleSubmit() {
    if (isError) {
      resetResolution()
      setUploadProgress({
        perimeter: { progress: 0 },
        mosaic: { progress: 0 },
      })
      resetUploadQueue()
      resetConfirmResolution()

      return
    }

    navigate(makeUrl('finish'))
  }

  const filesToUpload = useMemo(() => {
    const filesToUpload: FileToUpload[] = []

    if (resolution) {
      if (perimeterFromInput)
        filesToUpload.push({
          file: perimeterFromInput.zip,
          url: resolution.urls.perimeter,
          originalFilename: `${perimeterFromInput.results.filename}.zip`,
          inputType: 'perimeter',
          resumable: false,
          type: 'perimeter',
          done: perimeterFromInput.done || false,
        })

      if (mosaicFromInput)
        filesToUpload.push({
          file: mosaicFromInput.input,
          url: resolution.urls.mosaic,
          originalFilename: mosaicFromInput.input.name,
          inputType: 'mosaic',
          resumable: true,
          type: 'mosaic',
          done: mosaicFromInput.done || false,
        })
    }

    return filesToUpload
  }, [perimeterFromInput, resolution])

  useEffect(() => {
    if (isResolutionCreated) return
    if (!report?.id) return
    if (!perimeterFromInput && !mosaicFromInput) return

    createResolution({
      resolution: 'upload_file',
      resolved_by: user.id,
      files_in_resolution: {
        perimeter: perimeterFromInput?.results
          ? {
              area: perimeterFromInput.results.area,
              centroid: perimeterFromInput.results.centroid,
              filename: `${perimeterFromInput.results.filename}.zip`,
            }
          : undefined,
        mosaic: mosaicFromInput
          ? { filename: mosaicFromInput.input.name, ...mosaicFromInput.results }
          : undefined,
      },
    })
  }, [isResolutionCreated, report?.id])

  useEffect(() => {
    if (!isResolutionCreated) return

    uploadQueue({
      diagnosisId: diagnosisId!,
      filesToUpload: filesToUpload,
    })
  }, [isResolutionCreated, filesToUpload])

  useEffect(() => {
    if (!isUploadSuccessful) return

    confirmResolution()
  }, [isUploadSuccessful])

  if (!perimeterFromInput && !mosaicFromInput) {
    return <Navigate to={makeUrl('info')} />
  }

  const perimeter = perimeterFromInput?.results.geojson || perimeterData

  const isSuccessfull =
    isResolutionCreated && isUploadSuccessful && isConfirmResolutionSuccessfull
  const isError = isResolutionError || isUploadError || isConfirmResolutionError

  return (
    <Container className={classes.container}>
      <header className={classes.header}>
        <IconButton
          size='small'
          onClick={() => navigate(-1)}
          color='secondary'
          aria-label='Voltar'
        >
          <ArrowBack />
        </IconButton>

        <div>
          <Typography variant='h2'>Informações do diagnóstico</Typography>
          <Typography variant='h5'>
            Analise os pontos de atenção do diagnóstico
          </Typography>
        </div>
      </header>

      <div className={classes.content}>
        <div className={classes.resume}>
          {perimeterFromInput && (
            <LinearProgressWithLabel
              title='Perímetro - .shp + .shx + .dbf + .prj + .cpg ou .kml'
              label={perimeterFromInput?.shapefile
                .map((file) => file.name)
                .join(', ')}
              progressValue={uploadProgress.perimeter.progress}
              fileSize={bytesToUnitString(
                perimeterFromInput?.shapefile.reduce(
                  (acc, file) => acc + file.size,
                  0,
                ),
              )}
            />
          )}
          {mosaicFromInput && (
            <LinearProgressWithLabel
              title='Ortomosaico - .tif'
              label={mosaicFromInput.input.name}
              progressValue={uploadProgress.mosaic.progress}
              fileSize={bytesToUnitString(mosaicFromInput.input.size)}
            />
          )}
        </div>
        <div className={classes.map}>
          <Typography component={'span'}>Área cadastrada</Typography>

          <div>
            {!perimeterData || !(mosaicFromInput || cogUrl) ? (
              <Skeleton
                width={'100%'}
                height={'100%'}
                variant='rect'
                style={{ borderRadius: 4 }}
              />
            ) : (
              <Map
                perimeter={perimeter}
                thumbnail={mosaicFromInput?.input}
                cogUrl={cogUrl}
              />
            )}
          </div>
        </div>
      </div>

      <footer className={classes.footer}>
        <Button outlined onClick={() => navigate(-1)}>
          Voltar
        </Button>
        <Button onClick={handleSubmit} loading={!isSuccessfull && !isError}>
          {isError ? 'Tentar novamente' : 'Avançar'}
        </Button>
      </footer>
    </Container>
  )
}
